/**
 * Entity synchronization component for admin panel.
 * Allows admins to sync devices, clinics, and studies from external database.
 */

import { useState, useEffect } from 'react';
import { Button, Card, Spinner, Alert, Badge, Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';

interface SyncResult {
  inserted: number;
  updated: number;
}

interface SyncResponse {
  devices?: SyncResult;
  clinics?: SyncResult;
  studies?: SyncResult;
  message?: string;
}

interface ExternalDbConfig {
  enabled: boolean;
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  // Devices configuration
  devices_schema: string;
  devices_table: string;
  devices_id_column: string;
  // Clinics configuration
  clinics_schema: string;
  clinics_table: string;
  clinics_id_column: string;
  // Studies configuration
  studies_schema: string;
  studies_table: string;
  studies_id_column: string;
}

export default function EntitySync() {
  const { t } = useTranslation();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [config, setConfig] = useState<ExternalDbConfig>({
    enabled: false,
    host: '',
    port: 5432,
    database: '',
    username: '',
    password: '',
    devices_schema: 'public',
    devices_table: 'devices',
    devices_id_column: 'device_id',
    clinics_schema: 'public',
    clinics_table: 'clinics',
    clinics_id_column: 'clinic_id',
    studies_schema: 'public',
    studies_table: 'studies',
    studies_id_column: 'study_id',
  });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConfig, setTestingConfig] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (showConfigModal) {
      loadConfig();
    }
  }, [showConfigModal]);

  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const response = await apiClient.getExternalDbConfig();
      if (response.error) {
        throw new Error(response.error);
      }
      const configData = response.data as ExternalDbConfig;
      setConfig(configData);
    } catch (err) {
      toast.error(t('admin.failedToLoadConfig'));
      console.error(err);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSync = async (entityType?: 'devices' | 'clinics' | 'studies') => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    try {
      const response = await apiClient.syncExternalDatabase(entityType);
      if (response.error) {
        throw new Error(response.error);
      }

      const result = response.data as SyncResponse;
      setSyncResult(result);
      toast.success(result.message || t('admin.syncCompletedSuccess'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('admin.failedToSyncEntities');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    if (!config.host || !config.database || !config.username || !config.password) {
      toast.error(t('admin.fillRequiredFields'));
      return;
    }

    setTestingConfig(true);
    setTestResult(null);
    try {
      const response = await apiClient.testExternalDbConfig({
        host: config.host,
        port: config.port,
        database: config.database,
        username: config.username,
        password: config.password,
      });
      if (response.error) {
        throw new Error(response.error);
      }
      const result = response.data as { success: boolean; message: string };
      setTestResult(result);
      if (result.success) {
        toast.success(t('admin.connectionTestSuccess'));
      } else {
        toast.error(t('admin.connectionTestFailed'));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('admin.connectionTestFailed');
      setTestResult({ success: false, message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setTestingConfig(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const response = await apiClient.updateExternalDbConfig(config);
      if (response.error) {
        throw new Error(response.error);
      }
      toast.success(t('admin.configSavedSuccess'));
      setShowConfigModal(false);
      // Reload config to get the updated values
      await loadConfig();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('admin.failedToSaveConfig');
      toast.error(errorMessage);
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <div>
          <h2 className="mb-1">{t('admin.entitySyncTitle')}</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {t('admin.entitySyncDescription')}
          </p>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">{t('admin.syncOptions')}</h4>
          <Button variant="outline-secondary" onClick={() => setShowConfigModal(true)}>
            {t('admin.configureDatabase')}
          </Button>
        </Card.Header>
        <Card.Body>
          <div className="d-grid gap-2 d-md-flex">
            <Button
              variant="primary"
              onClick={() => handleSync()}
              disabled={syncing}
              className="me-2"
            >
              {syncing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('admin.syncing')}
                </>
              ) : (
                t('admin.syncAllEntities')
              )}
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => handleSync('devices')}
              disabled={syncing}
              className="me-2"
            >
              {t('admin.syncDevices')}
            </Button>
            <Button
              variant="outline-success"
              onClick={() => handleSync('clinics')}
              disabled={syncing}
              className="me-2"
            >
              {t('admin.syncClinics')}
            </Button>
            <Button variant="outline-info" onClick={() => handleSync('studies')} disabled={syncing}>
              {t('admin.syncStudies')}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <strong>{t('common.error')}:</strong> {error}
        </Alert>
      )}

      {syncResult && (
        <Card>
          <Card.Header>
            <h4 className="mb-0">{t('admin.syncResults')}</h4>
          </Card.Header>
          <Card.Body>
            {syncResult.message && (
              <Alert variant="success" className="mb-3">
                {syncResult.message}
              </Alert>
            )}

            <div className="row">
              {syncResult.devices && (
                <div className="col-md-4 mb-3">
                  <div className="border rounded p-3">
                    <h5>{t('admin.devices')}</h5>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{t('admin.inserted')}:</span>
                      <Badge bg="success">{syncResult.devices.inserted}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span>{t('admin.updated')}:</span>
                      <Badge bg="info">{syncResult.devices.updated}</Badge>
                    </div>
                  </div>
                </div>
              )}

              {syncResult.clinics && (
                <div className="col-md-4 mb-3">
                  <div className="border rounded p-3">
                    <h5>{t('admin.clinics')}</h5>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{t('admin.inserted')}:</span>
                      <Badge bg="success">{syncResult.clinics.inserted}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span>{t('admin.updated')}:</span>
                      <Badge bg="info">{syncResult.clinics.updated}</Badge>
                    </div>
                  </div>
                </div>
              )}

              {syncResult.studies && (
                <div className="col-md-4 mb-3">
                  <div className="border rounded p-3">
                    <h5>{t('admin.studies')}</h5>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{t('admin.inserted')}:</span>
                      <Badge bg="success">{syncResult.studies.inserted}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span>{t('admin.updated')}:</span>
                      <Badge bg="info">{syncResult.studies.updated}</Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.externalDbConfigTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingConfig ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={t('admin.enableExternalDbSync')}
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
              </Form.Group>

              <Alert variant="info" className="mb-3">
                <strong>{t('admin.note')}:</strong> {t('admin.postgresOnlyNote')}
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>{t('admin.host')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.host}
                  onChange={(e) => setConfig({ ...config, host: e.target.value })}
                  placeholder={t('admin.hostPlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('admin.port')} *</Form.Label>
                <Form.Control
                  type="number"
                  value={config.port}
                  onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value, 10) || 5432 })}
                  placeholder={t('admin.portPlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('admin.database')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.database}
                  onChange={(e) => setConfig({ ...config, database: e.target.value })}
                  placeholder={t('admin.databasePlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('admin.username')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.username}
                  onChange={(e) => setConfig({ ...config, username: e.target.value })}
                  placeholder={t('admin.usernamePlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t('admin.password')} *</Form.Label>
                <Form.Control
                  type="password"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  placeholder={t('admin.passwordPlaceholder')}
                />
              </Form.Group>

              <hr />

              <h5>{t('admin.devices')}</h5>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.schema')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.devices_schema}
                  onChange={(e) => setConfig({ ...config, devices_schema: e.target.value })}
                  placeholder="public"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.table')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.devices_table}
                  onChange={(e) => setConfig({ ...config, devices_table: e.target.value })}
                  placeholder={t('admin.devicesTablePlaceholder')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.idColumn')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.devices_id_column}
                  onChange={(e) => setConfig({ ...config, devices_id_column: e.target.value })}
                  placeholder="device_id"
                />
              </Form.Group>

              <hr />

              <h5>{t('admin.clinics')}</h5>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.schema')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.clinics_schema}
                  onChange={(e) => setConfig({ ...config, clinics_schema: e.target.value })}
                  placeholder="public"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.table')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.clinics_table}
                  onChange={(e) => setConfig({ ...config, clinics_table: e.target.value })}
                  placeholder={t('admin.clinicsTablePlaceholder')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.idColumn')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.clinics_id_column}
                  onChange={(e) => setConfig({ ...config, clinics_id_column: e.target.value })}
                  placeholder="clinic_id"
                />
              </Form.Group>

              <hr />

              <h5>{t('admin.studies')}</h5>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.schema')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.studies_schema}
                  onChange={(e) => setConfig({ ...config, studies_schema: e.target.value })}
                  placeholder="public"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.table')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.studies_table}
                  onChange={(e) => setConfig({ ...config, studies_table: e.target.value })}
                  placeholder={t('admin.studiesTablePlaceholder')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.idColumn')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={config.studies_id_column}
                  onChange={(e) => setConfig({ ...config, studies_id_column: e.target.value })}
                  placeholder="study_id"
                />
              </Form.Group>

              {testResult && (
                <Alert variant={testResult.success ? 'success' : 'danger'} className="mt-3">
                  {testResult.message}
                </Alert>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleTestConnection}
            disabled={testingConfig || loadingConfig}
          >
            {testingConfig ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('admin.testing')}
              </>
            ) : (
              t('admin.testConnection')
            )}
          </Button>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSaveConfig} disabled={savingConfig || loadingConfig}>
            {savingConfig ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('admin.saving')}
              </>
            ) : (
              t('admin.saveConfiguration')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
